import React from 'react';

import { Trans } from '@lingui/react/macro';
import { ListItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAtomValue } from 'jotai';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';

import { VideaTypography } from '../../../../../common-components/elements/VideaTypography/VideaTypography';
import {
  AREA_OF_CONCERN,
  CARIES,
  RADIOGRAPHIC_BONELOSS,
} from '../../../../../constants';
import { useIncipient } from '../../../../../routes/IncipientContext';
import { blueBlack630 } from '../../../../../theme/videa-style-tokens';
import { colors } from '../../../../../utils/theme';
import { isWithinSensitivityRange } from '../../../annotation-utils/incipient';
import { showSegmentationAtom } from '../../../multiFileViewer/imageToolbar/atoms';
import { useIsWithinConfidenceThreshold } from '../../confidenceScoreFilter/helpers';

const useStyles = makeStyles((theme) => ({
  listItem: {
    display: 'flex',
    padding: '.5em 1.5em',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'white',
    '&:hover': {
      background: 'rgba(255,255,255,0.1)',
      cursor: 'pointer',
    },
  },
  hoveredItem: {
    background: 'rgba(255,255,255,0.1)',
    cursor: 'pointer',
  },
  listHeader: {
    display: 'flex',
    padding: theme.spacing(1),
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 0,
  },
  number: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    backgroundColor: blueBlack630,
    borderRadius: '1.5em',
    textAlign: 'center',
    padding: '.25em',
    width: '1.5em',
    height: '1.5em',
    justifySelf: 'flex-start',
    fontWeight: '700',
  },
  findings: {
    paddingLeft: '10px',
    color: 'white',
  },
  image: {
    color: `${colors.LIGHT_GREY}`,
    marginLeft: 'auto',
  },
}));

const ImageItem = (props: any) => {
  const {
    findingGroup = {},
    image = {},
    index = 0,
    currentlyHoveredImageId = '',
    onSetCurrentlyHoveredImageId = () => {},
  } = props;

  // @ts-expect-error TS(2339): Property 'incipientThresholdRange' does not exist ... Remove this comment to see the full error message
  const { incipientThresholdRange } = useIncipient();
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const history = useHistory();
  const showSegmentations = useAtomValue(showSegmentationAtom);

  const navigateToImage = () => {
    if (!image?.imageId) {
      return;
    }
    const newRoute = `${history.location.pathname}/${image.imageId}`;
    history.push({ pathname: newRoute, search: history.location.search });
  };

  const findingsForImage = findingGroup.findings.filter(
    (finding: any) => finding.sourceImageId === image.imageId,
  );
  // For RBL don't count center sides or sides with no points
  let numberOfFindings = findingsForImage.length;
  if (findingGroup.type === RADIOGRAPHIC_BONELOSS) {
    numberOfFindings = findingsForImage.filter(
      (finding: any) => finding.side !== 'center' && finding.points.length >= 2,
    )?.length;
  }
  const numberOfHiddenFindings =
    findingsForImage?.filter((f: any) => {
      let shouldHideFinding = !isWithinConfidenceThreshold(
        f.type,
        f.confidenceScore,
        f?.bin,
      );
      if (
        !shouldHideFinding &&
        (findingGroup.type === CARIES || findingGroup.type === AREA_OF_CONCERN)
      ) {
        shouldHideFinding =
          f.validation === 'Disagree' ||
          (f.source === 'AI' &&
            !isWithinSensitivityRange(f, image, incipientThresholdRange));
      }
      return shouldHideFinding;
    })?.length || 0;

  const classes = useStyles();
  return (numberOfFindings > 0 || numberOfHiddenFindings) > 0 ? (
    <ListItem
      color="info"
      className={
        image.objectId === currentlyHoveredImageId
          ? `${classes.listItem} ${classes.hoveredItem}`
          : classes.listItem
      }
      onMouseEnter={() => onSetCurrentlyHoveredImageId(image.objectId)}
      onMouseLeave={() => onSetCurrentlyHoveredImageId('')}
      onClick={navigateToImage}
    >
      <VideaTypography variant="bodyExtrasmall" className={classes.number}>
        {numberOfFindings}
      </VideaTypography>
      <VideaTypography variant="bodyExtrasmall" className={classes.findings}>
        <Trans>Findings</Trans>{' '}
        {numberOfHiddenFindings > 0 && !showSegmentations ? (
          <Trans>({numberOfHiddenFindings} hidden)</Trans>
        ) : (
          ''
        )}
      </VideaTypography>
      <VideaTypography variant="bodyExtrasmall" className={classes.image}>
        <Trans>Image {index}</Trans>
      </VideaTypography>
    </ListItem>
  ) : null;
};

ImageItem.propTypes = {
  image: PropTypes.object,
  findingGroup: PropTypes.object,
  index: PropTypes.number,
  currentlyHoveredImageId: PropTypes.string,
  user: PropTypes.object,
  onSetCurrentlyHoveredImageId: PropTypes.func,
};

export default ImageItem;
